import type {Byte, Overflow} from '~/types/legacy'
import type {Populated} from '~/types/populated'
import {getRequestIP} from 'h3'

interface ApiResponse<T> {
  data: T
}

interface ApiByte extends Byte {
  liked: boolean
}

// name comes from "webamboos overflow" where each post is called a "technical byte"
export function useOverflow() {
  const {payloadUrl} = useRuntimeConfig()
  const route = useRoute()

  const baseURL = import.meta.dev ? 'http://localhost:3000/api' : payloadUrl || '/api'

  // Get client IP during SSR
  const event = useRequestEvent()
  const clientIp = event
    ? getRequestIP(event, {xForwardedFor: true}) || event.node.req.socket.remoteAddress
    : undefined

  const fetchHero = async (): Promise<Populated<Overflow>> => {
    const sdk = usePayloadSdk()
    const result = await sdk.findGlobal({
      slug: 'overflowGlobal',
      depth: 1,
      draft: !!route.query.preview,
    })

    return result as Populated<Overflow>
  }

  const fetchBytes = async (sort: 'recent' | 'liked' = 'recent'): Promise<Populated<ApiByte>[]> => {
    const params = new URLSearchParams()

    params.set('sort', sort)
    if (route.query.preview) {
      params.set('preview', 'true')
    }

    const response = await $fetch<ApiResponse<ApiByte[]>>(`/bytes?${params.toString()}`, {
      baseURL,
      headers: clientIp ? {'x-forwarded-for': clientIp} : undefined,
    })

    return response.data as Populated<ApiByte>[]
  }

  const fetchBySlug = async (slug: string): Promise<Populated<ApiByte> | null> => {
    const params = new URLSearchParams()
    params.set('slug', slug)
    if (route.query.preview) {
      params.set('preview', 'true')
    }

    const response = await $fetch<ApiResponse<ApiByte>>(`/bytes?${params.toString()}`, {
      baseURL,
      headers: clientIp ? {'x-forwarded-for': clientIp} : undefined,
    })

    return response.data as Populated<ApiByte>
  }

  return {
    fetchHero,
    fetchBytes,
    fetchBySlug,
  }
}
